import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Tag, Row, Col, Table, Input, Select, Spin, Empty, message, Modal, Tooltip, Progress } from "antd";
import {
  ProjectOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Option } = Select;

const PageHeader = styled.div`
  margin-bottom: 24px;
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 8px 0; }
  p { color: #666; margin: 0; font-size: 14px; }
`;

const AdminEngagementsWrapper = styled.div`
  min-height: 100vh;

  .metrics-row {
    margin-bottom: 24px;

    .metric-card {
      border-radius: 12px;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      .metric-content {
        display: flex;
        align-items: center;
        gap: 16px;

        .metric-icon {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .metric-info {
          .metric-value {
            font-size: 28px;
            font-weight: 700;
            color: #014c75;
            margin: 0;
            line-height: 1.2;
          }

          .metric-label {
            font-size: 14px;
            color: #8c8c8c;
            margin: 0;
          }
        }
      }
    }
  }

  .tabs-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;

    .tab-button {
      border: none;
      background: transparent;
      padding: 8px 20px;
      border-radius: 8px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;

      &.active {
        background: #01d9a9;
        color: white;
      }

      &:hover:not(.active) {
        background: #f0f0f0;
      }
    }
  }

  .filters-section {
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
    flex-wrap: wrap;
  }

  .engagement-title-link {
    color: #014c75;
    font-weight: 500;
    cursor: pointer;

    &:hover {
      color: #01d9a9;
    }
  }

  .org-link {
    color: #1890ff;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .action-buttons {
    display: flex;
    gap: 8px;
  }
`;

const statusColors = {
  pending_approval: "orange",
  active: "green",
  closure_requested: "gold",
  closed: "default",
};

const statusLabels = {
  pending_approval: "Pending Approval",
  active: "Active",
  closure_requested: "Closure Requested",
  closed: "Closed",
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return `₹ ${parseFloat(value).toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const AdminEngagements = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("active");
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [stats, setStats] = useState({
    total: 0,
    pendingApproval: 0,
    active: 0,
    closureRequested: 0,
    closed: 0,
  });

  const fetchEngagements = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const status = activeTab;
      
      const response = await axios.post(
        API_CONST.GET_ENGAGEMENTS,
        { status, search: searchText, limit: 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setEngagements(response.data.engagements || []);
    } catch (error) {
      console.error("Error fetching engagements:", error);
      message.error("Failed to load engagements");
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchText]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_ENGAGEMENT_STATS,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data.stats || {});
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchEngagements();
    fetchStats();
  }, [fetchEngagements, fetchStats]);

  const handleViewDetails = (id) => {
    navigate(`/admin/engagements/details/${id}`);
  };

  const handleApprove = (engagement) => {
    Modal.confirm({
      title: "Approve Engagement",
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      content: (
        <div>
          <p>Are you sure you want to approve this engagement?</p>
          <p style={{ fontWeight: 500 }}>{engagement.title}</p>
          <p style={{ color: "#8c8c8c", fontSize: 13 }}>
            This will allow the customer to add jobs and hire talents.
          </p>
        </div>
      ),
      okText: "Approve",
      okButtonProps: { style: { background: "#52c41a", borderColor: "#52c41a" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.post(
            `${API_CONST.APPROVE_ENGAGEMENT}/${engagement.id}/approve`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Engagement approved successfully");
          fetchEngagements();
          fetchStats();
        } catch (error) {
          message.error(error.response?.data?.message || "Failed to approve engagement");
        }
      },
    });
  };

  const handleReject = (engagement) => {
    Modal.confirm({
      title: "Reject Engagement",
      icon: <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />,
      content: (
        <div>
          <p>Are you sure you want to reject this engagement?</p>
          <p style={{ fontWeight: 500 }}>{engagement.title}</p>
        </div>
      ),
      okText: "Reject",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.post(
            `${API_CONST.APPROVE_ENGAGEMENT}/${engagement.id}/reject`,
            { reason: "Rejected by admin" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Engagement rejected");
          fetchEngagements();
          fetchStats();
        } catch (error) {
          message.error(error.response?.data?.message || "Failed to reject engagement");
        }
      },
    });
  };

  const handleCloseClosure = (engagement) => {
    Modal.confirm({
      title: "Close Engagement",
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      content: (
        <div>
          <p>Are you sure you want to close this engagement?</p>
          <p style={{ fontWeight: 500 }}>{engagement.title}</p>
          {engagement.closureReason && (
            <p style={{ color: "#8c8c8c", fontSize: 13 }}>
              Customer reason: {engagement.closureReason}
            </p>
          )}
        </div>
      ),
      okText: "Close Engagement",
      okButtonProps: { style: { background: "#52c41a", borderColor: "#52c41a" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.post(
            `${API_CONST.CLOSE_ENGAGEMENT}/${engagement.id}/close`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Engagement closed successfully");
          fetchEngagements();
          fetchStats();
        } catch (error) {
          message.error(error.response?.data?.message || "Failed to close engagement");
        }
      },
    });
  };

  const handleRejectClosure = (engagement) => {
    Modal.confirm({
      title: "Reject Closure Request",
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: (
        <div>
          <p>Are you sure you want to reject the closure request?</p>
          <p style={{ fontWeight: 500 }}>{engagement.title}</p>
          <p style={{ color: "#8c8c8c", fontSize: 13 }}>
            The engagement will remain active.
          </p>
        </div>
      ),
      okText: "Reject Closure",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.post(
            `${API_CONST.REJECT_ENGAGEMENT_CLOSURE}/${engagement.id}/reject-closure`,
            { reason: "Rejected by admin" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Closure request rejected - engagement remains active");
          fetchEngagements();
          fetchStats();
        } catch (error) {
          message.error(error.response?.data?.message || "Failed to reject closure");
        }
      },
    });
  };

  const handleCloseDirectly = (engagement) => {
    Modal.confirm({
      title: "Close Engagement",
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: (
        <div>
          <p>Are you sure you want to close this engagement?</p>
          <p style={{ fontWeight: 500 }}>{engagement.title}</p>
          <p style={{ color: "#8c8c8c", fontSize: 13 }}>
            This will mark the engagement as closed.
          </p>
        </div>
      ),
      okText: "Close Engagement",
      okButtonProps: { style: { background: "#1890ff", borderColor: "#1890ff" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.post(
            `${API_CONST.CLOSE_ENGAGEMENT}/${engagement.id}/close`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Engagement closed successfully");
          fetchEngagements();
          fetchStats();
        } catch (error) {
          message.error(error.response?.data?.message || "Failed to close engagement");
        }
      },
    });
  };

  const handleDeleteEngagement = (engagement) => {
    Modal.confirm({
      title: "Delete Engagement",
      icon: <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />,
      content: (
        <div>
          <p>Are you sure you want to permanently delete this engagement?</p>
          <p style={{ fontWeight: 500 }}>{engagement.title}</p>
          <p style={{ color: "#ff4d4f", fontSize: 13 }}>
            This action cannot be undone.
          </p>
        </div>
      ),
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.delete(
            `${API_CONST.DELETE_ENGAGEMENT}/${engagement.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Engagement deleted successfully");
          fetchEngagements();
          fetchStats();
        } catch (error) {
          message.error(error.response?.data?.message || "Failed to delete engagement");
        }
      },
    });
  };

  const tableColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 150,
      render: (id) => <span style={{ fontSize: 12, color: "#8c8c8c" }}>{id}</span>,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title, record) => (
        <span className="engagement-title-link" onClick={() => handleViewDetails(record.id)}>
          {title}
        </span>
      ),
    },
    {
      title: "Customer",
      key: "organization",
      width: 180,
      render: (_, record) => (
        <span
          className="org-link"
          onClick={() => navigate(`/admin/organization/${record.organizationId}`)}
        >
          {record.organizationName || "-"}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "engagementType",
      key: "engagementType",
      width: 140,
      render: (type) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>,
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      width: 130,
      render: (v) => v || "-",
    },
    {
      title: "Budget",
      key: "budget",
      width: 220,
      render: (_, record) => {
        const totalBudget = parseFloat(record.totalBudget) || 0;
        const budgetSpent = parseFloat(record.budgetSpent) || 0;
        const percent = totalBudget > 0 ? Math.min((budgetSpent / totalBudget) * 100, 100) : 0;
        const strokeColor = percent >= 90 ? '#ff4d4f' : percent >= 70 ? '#faad14' : '#52c41a';
        
        return (
          <div style={{ minWidth: 180 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: '#8c8c8c' }}>Spent: {formatCurrency(budgetSpent)}</span>
              <span style={{ color: '#8c8c8c' }}>/ {formatCurrency(totalBudget)}</span>
            </div>
            <Progress
              percent={Math.round(percent)}
              size="small"
              strokeColor={strokeColor}
              format={(pct) => `${pct}%`}
            />
          </div>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: "Action",
      key: "action",
      width: 200,
      fixed: "right",
      render: (_, record) => (
        <div className="action-buttons">
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewDetails(record.id)} />
          </Tooltip>
          {record.status === "active" && (
            <>
              <Tooltip title="Edit Engagement">
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/admin/engagements/edit/${record.id}`)}
                />
              </Tooltip>
              <Tooltip title="Close Engagement">
                <Button
                  size="small"
                  icon={<StopOutlined />}
                  style={{ color: "#1890ff", borderColor: "#1890ff" }}
                  onClick={() => handleCloseDirectly(record)}
                />
              </Tooltip>
            </>
          )}
          {record.status === "closure_requested" && (
            <>
              <Tooltip title="Approve Closure">
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckOutlined />}
                  style={{ background: "#52c41a", borderColor: "#52c41a" }}
                  onClick={() => handleCloseClosure(record)}
                />
              </Tooltip>
              <Tooltip title="Reject Closure">
                <Button
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => handleRejectClosure(record)}
                />
              </Tooltip>
            </>
          )}
          {record.status === "closed" && (
            <Tooltip title="Delete Engagement">
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteEngagement(record)}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <AdminEngagementsWrapper>
      <PageHeader><h1>Engagements</h1><p>Track and manage client engagements</p></PageHeader>

      <Row gutter={16} className="metrics-row">
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable onClick={() => setActiveTab("active")}>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#f6ffed" }}>
                <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{stats.active || 0}</h3>
                <p className="metric-label">Active Engagements</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable onClick={() => setActiveTab("pending_approval")}>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#fff7e6" }}>
                <ClockCircleOutlined style={{ color: "#fa8c16", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{stats.pendingApproval || 0}</h3>
                <p className="metric-label">Pending Approval</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#fff1f0" }}>
                <DollarOutlined style={{ color: "#cf1322", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{formatCurrency(stats.totalSpent)}</h3>
                <p className="metric-label">Total Spent</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#f0f5ff" }}>
                <DollarOutlined style={{ color: "#597ef7", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{formatCurrency(stats.totalBudget)}</h3>
                <p className="metric-label">Total Budget</p>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <div className="tabs-section">
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            className={`tab-button ${activeTab === "active" ? "active" : ""}`}
            onClick={() => setActiveTab("active")}
          >
            Active ({stats.active || 0})
          </Button>
          <Button
            className={`tab-button ${activeTab === "closure_requested" ? "active" : ""}`}
            onClick={() => setActiveTab("closure_requested")}
          >
            Closure Requested ({stats.closureRequested || 0})
          </Button>
          <Button
            className={`tab-button ${activeTab === "closed" ? "active" : ""}`}
            onClick={() => setActiveTab("closed")}
          >
            Closed ({stats.closed || 0})
          </Button>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/admin/engagements/create")}
        >
          Create Engagement
        </Button>
      </div>

      <div className="filters-section">
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search engagements..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </div>

      <Spin spinning={loading}>
        <Table
          columns={tableColumns}
          dataSource={engagements}
          rowKey="id"
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
          scroll={{ x: 1200 }}
          locale={{ emptyText: <Empty description="No engagements found" /> }}
        />
      </Spin>
    </AdminEngagementsWrapper>
  );
};

export default AdminEngagements;
